'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { BriefcaseBusiness, ExternalLink, FolderKanban, ShieldCheck } from 'lucide-react';
import { useTranslation } from '@/i18n/useTranslation';

type Career = {
  id: string;
  title: string;
  organization: string | null;
  start_date: string;
  end_date: string | null;
  is_current: boolean;
  location: string | null;
  description: string | null;
  institution_confirmation_record_id: string | null;
};

type Chapter = {
  id: string;
  title: string;
  description: string | null;
  start_date: string | null;
  end_date: string | null;
  accent_color: string;
  recordCount: number;
};

type FeaturedProject = {
  id: string;
  title: string;
  project_type: string;
  summary: string | null;
  role_label: string | null;
  organization: string | null;
  start_date: string | null;
  end_date: string | null;
  status: string;
  external_url: string | null;
};

type Payload = {
  currentRole: Career | null;
  professionalEnabled: boolean;
  career?: Career[];
  chapters?: Chapter[];
  featuredProjects?: FeaturedProject[];
};

function period(start: string | null, end: string | null, current: boolean, present: string) {
  return [start?.slice(0, 4), current ? present : end?.slice(0, 4)].filter(Boolean).join(' – ');
}

function titleCase(value: string) {
  return value.replaceAll('_', ' ').replace(/\b\w/g, (character) => character.toUpperCase());
}

function usePublicLifeRecord(handle: string, history: boolean) {
  const [data, setData] = useState<Payload | null>(null);
  useEffect(() => {
    const controller = new AbortController();
    fetch(
      `/api/public/life-record?handle=${encodeURIComponent(handle)}${history ? '&history=1' : ''}`,
      { cache: 'no-store', signal: controller.signal }
    )
      .then((response) => (response.ok ? response.json() : null))
      .then((payload) => {
        if (payload) setData(payload as Payload);
      })
      .catch(() => undefined);
    return () => controller.abort();
  }, [handle, history]);
  return data;
}

export function PublicCurrentRole({ handle }: { handle: string }) {
  const { safeT } = useTranslation();
  const data = usePublicLifeRecord(handle, false);
  if (!data || (!data.currentRole && !data.professionalEnabled && !data.featuredProjects?.length)) {
    return null;
  }

  const featuredProjects = data.featuredProjects?.slice(0, 3) || [];
  return (
    <div className="mt-3 space-y-3">
      {data.currentRole && (
        <div className="flex flex-wrap items-center gap-2 text-sm font-semibold text-[var(--muted)]">
          <BriefcaseBusiness size={14} aria-hidden />
          <span>
            {data.currentRole.title}
            {data.currentRole.organization ? ` · ${data.currentRole.organization}` : ''}
          </span>
          <span className="inline-flex items-center gap-1 rounded-full border border-[var(--line)] px-2 py-0.5 text-[10px] font-semibold">
            <ShieldCheck size={10} aria-hidden />
            {data.currentRole.institution_confirmation_record_id
              ? safeT('life_record.institution_confirmed' as never, 'Institution-confirmed')
              : safeT('life_record.self_recorded' as never, 'Self-recorded')}
          </span>
        </div>
      )}
      {featuredProjects.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 text-xs text-[var(--muted)]">
          <span className="inline-flex items-center gap-1 font-semibold">
            <FolderKanban size={13} aria-hidden />
            {safeT('life_record.featured_projects' as never, 'Featured work')}
          </span>
          {featuredProjects.map((project) =>
            project.external_url ? (
              <a
                key={project.id}
                href={project.external_url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex min-h-7 items-center gap-1 rounded-full border border-[var(--line)] px-2.5 font-semibold text-[var(--green)] transition-colors duration-150 hover:bg-[var(--paper)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--green)]"
              >
                {project.title} <ExternalLink size={10} aria-hidden />
              </a>
            ) : (
              <span
                key={project.id}
                className="inline-flex min-h-7 items-center rounded-full border border-[var(--line)] px-2.5 font-semibold"
              >
                {project.title}
              </span>
            )
          )}
        </div>
      )}
      {data.professionalEnabled && (
        <Link
          href={`/u/${handle}?view=professional`}
          className="inline-flex min-h-8 items-center text-sm font-semibold text-[var(--green)] hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--green)]"
        >
          {safeT('life_record.open_professional' as never, 'Open Professional View')}
        </Link>
      )}
    </div>
  );
}

export function PublicCareerAndChapters({
  handle,
  showCareer = true,
}: {
  handle: string;
  showCareer?: boolean;
}) {
  const { safeT } = useTranslation();
  const data = usePublicLifeRecord(handle, true);
  if (!data || (!data.career?.length && !data.chapters?.length && !data.featuredProjects?.length)) {
    return null;
  }
  const present = safeT('life_record.present' as never, 'Present');

  return (
    <div className="space-y-8" style={{ marginBottom: 28 }}>
      {showCareer && Boolean(data.career?.length) && (
        <section>
          <h3 className="text-xs font-bold uppercase tracking-[.14em] text-[var(--muted)]">
            {safeT('life_record.career' as never, 'Career')}
          </h3>
          <div className="mt-3 divide-y divide-[var(--line)] border-y border-[var(--line)]">
            {data.career!.map((entry) => (
              <article key={entry.id} className="grid gap-2 py-4 sm:grid-cols-[120px_1fr]">
                <p className="text-xs font-semibold text-[var(--muted)]">
                  {period(entry.start_date, entry.end_date, entry.is_current, present)}
                </p>
                <div>
                  <h4 className="font-semibold text-[var(--ink)]">{entry.title}</h4>
                  {entry.organization && (
                    <p className="text-sm text-[var(--muted)]">{entry.organization}</p>
                  )}
                  {entry.description && (
                    <p className="mt-2 text-sm leading-6 text-[var(--muted)]">
                      {entry.description}
                    </p>
                  )}
                  <p className="mt-2 inline-flex items-center gap-1 text-[10px] font-semibold text-[var(--muted)]">
                    <ShieldCheck size={10} aria-hidden />
                    {entry.institution_confirmation_record_id
                      ? safeT('life_record.institution_confirmed' as never, 'Institution-confirmed')
                      : safeT('life_record.self_recorded' as never, 'Self-recorded')}
                  </p>
                </div>
              </article>
            ))}
          </div>
        </section>
      )}

      {Boolean(data.featuredProjects?.length) && (
        <section>
          <h3 className="text-xs font-bold uppercase tracking-[.14em] text-[var(--muted)]">
            {safeT('life_record.featured_projects' as never, 'Featured projects')}
          </h3>
          <div className="mt-3 divide-y divide-[var(--line)] border-y border-[var(--line)]">
            {data.featuredProjects!.map((project) => (
              <article key={project.id} className="grid gap-2 py-4 sm:grid-cols-[120px_1fr]">
                <p className="text-xs font-semibold text-[var(--muted)]">
                  {period(
                    project.start_date,
                    project.end_date,
                    project.status === 'ongoing',
                    present
                  )}
                </p>
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-[.12em] text-[var(--green)]">
                    {safeT(
                      `life_record.project_type_${project.project_type}` as never,
                      titleCase(project.project_type)
                    )}
                  </p>
                  <h4 className="mt-1 font-semibold text-[var(--ink)]">{project.title}</h4>
                  {(project.role_label || project.organization) && (
                    <p className="text-sm text-[var(--muted)]">
                      {[project.role_label, project.organization].filter(Boolean).join(' · ')}
                    </p>
                  )}
                  {project.summary && (
                    <p className="mt-2 text-sm leading-6 text-[var(--muted)]">{project.summary}</p>
                  )}
                  {project.external_url && (
                    <a
                      href={project.external_url}
                      target="_blank"
                      rel="noreferrer"
                      className="mt-2 inline-flex min-h-8 items-center gap-1 text-xs font-semibold text-[var(--green)] hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--green)]"
                    >
                      {safeT('life_record.visit_work' as never, 'Visit work')}
                      <ExternalLink size={11} aria-hidden />
                    </a>
                  )}
                </div>
              </article>
            ))}
          </div>
        </section>
      )}

      {Boolean(data.chapters?.length) && (
        <section>
          <h3 className="text-xs font-bold uppercase tracking-[.14em] text-[var(--muted)]">
            {safeT('life_record.chapters' as never, 'Life Chapters')}
          </h3>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            {data.chapters!.map((chapter) => (
              <article
                key={chapter.id}
                className="rounded-xl border border-[var(--line)] p-4"
                style={{ borderTopColor: chapter.accent_color, borderTopWidth: 3 }}
              >
                <p className="text-xs font-semibold text-[var(--muted)]">
                  {period(chapter.start_date, chapter.end_date, false, present)}
                </p>
                <h4 className="mt-1 font-semibold text-[var(--ink)]">{chapter.title}</h4>
                {chapter.description && (
                  <p className="mt-2 line-clamp-3 text-sm leading-6 text-[var(--muted)]">
                    {chapter.description}
                  </p>
                )}
                <p className="mt-3 text-[10px] font-bold text-[var(--muted)]">
                  {chapter.recordCount}{' '}
                  {safeT('life_record.related_records' as never, 'related records')}
                </p>
              </article>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
